import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Contact() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    company: "",
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    if (!form.name || !form.email || !form.message) {
      toast.error("Please complete the required fields.");
      return;
    }
    if (form.message.length < 5) {
      toast.error("Please add a bit more detail.");
      return;
    }
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/contact`, form);
      if (res.data?.status === "success") {
        setSent(true);
        toast.success("Message received. Reply within one working day.");
        setForm({ name: "", email: "", company: "", message: "" });
      } else {
        toast.error("Could not send. Please email directly.");
      }
    } catch (err) {
      toast.error("Could not send. Please email saran@sarangandhi.com directly.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="relative border-b border-rule bg-ink text-paper"
    >
      <div className="max-w-[1400px] mx-auto grid grid-cols-1 lg:grid-cols-12 gap-0">
        {/* LEFT */}
        <div className="lg:col-span-5 px-6 md:px-12 lg:px-14 py-20 md:py-24 lg:border-r lg:border-paper/15">
          <div className="overline text-paper/60 mb-6 reveal">Contact — 08</div>

          <h2
            data-testid="contact-heading"
            className="serif text-4xl md:text-5xl lg:text-6xl font-light tracking-tight leading-[0.98] text-paper reveal"
          >
            A 30-minute conversation.
          </h2>

          <p className="mt-6 text-base text-paper/75 max-w-md reveal">
            We discuss what you are trying to do and whether we are the right
            partner. No sales process.
          </p>

          <div className="mt-10 reveal">
            <a
              href="https://calendly.com/sarangandhi1/30min"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="contact-calendly-btn"
              className="inline-flex items-center justify-center gap-3 bg-mustard text-ink px-7 py-4 text-sm tracking-wide hover:bg-paper hover:text-ink transition-colors duration-300 group w-full sm:w-auto"
            >
              Book a 30-minute call
              <span className="transition-transform duration-300 group-hover:translate-x-1">→</span>
            </a>
          </div>

          <div className="mt-12 pt-10 border-t border-paper/15 reveal">
            <div className="overline text-paper/60 mb-5">Direct</div>
            <dl className="space-y-3 text-paper">
              <DirectRow k="Email" v="saran@sarangandhi.com" href="mailto:saran@sarangandhi.com" testid="contact-email-link" />
              <DirectRow k="Phone" v="+44 7878 781 100" href="tel:+447878781100" testid="contact-phone-link" mono />
              <div className="flex items-baseline gap-4 text-paper/80">
                <span className="overline text-paper/60 w-16">Based</span>
                <span className="text-sm">London, UK · Working across UK &amp; EU</span>
              </div>
            </dl>
          </div>
        </div>

        {/* RIGHT */}
        <div className="lg:col-span-7 px-6 md:px-12 lg:px-14 py-20 md:py-24 bg-ink">
          <div className="overline text-paper/60 mb-6 reveal">Send a note</div>

          {sent ? (
            <div data-testid="contact-success-state" className="border border-paper/20 p-10">
              <div className="serif text-3xl font-light mb-3 text-paper">Thank you.</div>
              <p className="text-paper/75">Your message is in. Reply within one working day.</p>
              <button
                onClick={() => setSent(false)}
                data-testid="contact-send-another"
                className="mt-8 overline text-paper/80 hover:text-brass transition-colors"
              >
                Send another →
              </button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-5 reveal" data-testid="contact-form">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <Field id="name" label="Name" value={form.name} onChange={handleChange} required testid="contact-input-name" />
                <Field id="email" label="Email" type="email" value={form.email} onChange={handleChange} required testid="contact-input-email" />
              </div>
              <Field id="company" label="Company (optional)" value={form.company} onChange={handleChange} testid="contact-input-company" />
              <Field id="message" label="What you are trying to do" value={form.message} onChange={handleChange} textarea required testid="contact-input-message" />

              <div className="pt-3">
                <button
                  type="submit"
                  disabled={submitting}
                  data-testid="contact-submit-btn"
                  className="inline-flex items-center justify-center gap-3 bg-mustard text-ink px-7 py-4 text-sm tracking-wide hover:bg-paper hover:text-ink transition-colors duration-300 disabled:opacity-50 disabled:cursor-not-allowed group w-full sm:w-auto"
                >
                  {submitting ? "Sending…" : "Send message"}
                  {!submitting && <span className="transition-transform duration-300 group-hover:translate-x-1">→</span>}
                </button>
                <div className="overline text-paper/60 mt-5">Replies within 1 working day · No newsletters</div>
              </div>
            </form>
          )}
        </div>
      </div>
    </section>
  );
}

function DirectRow({ k, v, href, testid, mono }) {
  return (
    <a
      href={href}
      data-testid={testid}
      className="group flex items-baseline gap-4 text-paper hover:text-brass transition-colors"
    >
      <span className="overline text-paper/60 w-16 group-hover:text-brass transition-colors">{k}</span>
      <span className={`text-base ${mono ? "mono" : ""}`}>{v}</span>
    </a>
  );
}

function Field({ id, label, value, onChange, type = "text", textarea, required, testid }) {
  const common =
    "w-full bg-paper/[0.06] border border-paper/20 focus:border-brass focus:bg-paper/10 text-paper placeholder-paper/30 px-4 py-3 outline-none transition-colors duration-200 font-sans text-base";
  return (
    <div>
      <label htmlFor={id} className="overline text-paper/70 block mb-2">
        {label} {required && <span className="text-brass">*</span>}
      </label>
      {textarea ? (
        <textarea id={id} name={id} value={value} onChange={onChange} required={required} rows={4} data-testid={testid} className={common + " resize-none"} />
      ) : (
        <input id={id} name={id} type={type} value={value} onChange={onChange} required={required} data-testid={testid} className={common} />
      )}
    </div>
  );
}
